import { BehaviorSubject } from "rxjs";
import { v4 as uuid } from "uuid";
import { displayOrderVehicleIdentifier } from "../display/vehicle-identifier";
import { addComputedPropertiesToOrder } from "./functions";
import { Logger } from "../logger";
const {
  error: reportError
} = new Logger("QqOrder");
const DefaultOrderFields = {
  serviceItemId: "",
  orderStatus: "PENDING",
  orderType: "STORE",
  hasManualDiscount: false,
  isNewVehicle: false,
  isNewMember: false,
  isRewash: false,
  isNewCustomer: false,
  isEmployee: false,
  totalTaxable: "0",
  totalMarkDowns: "0",
  totalMarkUps: "0",
  subTotal: "0",
  taxTotal: "0",
  orderTotal: "0",
  submissionAt: "",
  submissionTime: "",
  addresses: [],
  holds: [],
  items: [],
  payments: [],
  markDowns: [],
  markUps: [],
  orderNumber: "",
  xrefId: 0,
  receiptNumber: "",
  birthAt: "",
  gender: "UNKNOWN",
  mbrCouponIssuedName: "",
  memberExpiresAt: "",
  orgOrderId: "",
  totalMbrRedemptions: "",
  totalFlockDiscounts: "",
  completedIn: 0,
  refundedAt: "",
  canceledAt: ""
};
let QqObservableOrder = /*#__PURE__*/(() => {
  class QqObservableOrder extends BehaviorSubject {
    constructor(seed) {
      if (!seed.customerId) {
        reportError("constructor", "trying to create QqOrder without customerId", {
          seed
        });
        throw new Error("trying to create QqOrder without customerId");
      }
      super(addComputedPropertiesToOrder(Object.assign(Object.assign({
        orderId: uuid()
      }, DefaultOrderFields), seed)));
    }
    get hasFlockAddon() {
      return this.value.hasFlockAddon;
    }
    get hasWashItem() {
      return this.value.hasWashItem;
    }
    get hasSubscriptionItem() {
      return this.value.hasSubscriptionItem;
    }
    get hasMembershipItem() {
      return this.value.hasMembershipItem;
    }
    get hasAuthorizedCreditCardPayment() {
      return this.value.hasAuthorizedCreditCardPayment;
    }
    get hasPaidCreditCardPayment() {
      return this.value.hasPaidCreditCardPayment;
    }
    get hasTokenizedCreditCardPayment() {
      return this.value.hasTokenizedCreditCardPayment;
    }
    get canComplete() {
      return this.value.canComplete;
    }
    get remoteError() {
      return this.value.remoteError;
    }
    get serviceItemId() {
      return this.value.serviceItemId;
    }
    get isNewMember() {
      return this.value.isNewMember;
    }
    get isRewash() {
      return this.value.isRewash;
    }
    get hasManualDiscount() {
      return this.value.hasManualDiscount;
    }
    get isNewVehicle() {
      return this.value.isNewVehicle;
    }
    get orderId() {
      return this.value.orderId;
    }
    get storeId() {
      return this.value.storeId;
    }
    get regionId() {
      return this.value.regionId;
    }
    get customerId() {
      return this.value.customerId;
    }
    get customerType() {
      return this.value.customerType;
    }
    get cashierId() {
      return this.value.cashierId;
    }
    get deviceId() {
      return this.value.deviceId;
    }
    get membershipId() {
      return this.value.membershipId;
    }
    get stripeCustomerId() {
      return this.value.stripeCustomerId;
    }
    get mbrCouponIssuedId() {
      return this.value.mbrCouponIssuedId;
    }
    get campaignId() {
      return this.value.campaignId;
    }
    get originalOrderId() {
      return this.value.originalOrderId;
    }
    get createReasonCode() {
      return this.value.createReasonCode;
    }
    get lane() {
      return this.value.lane;
    }
    get vehicleId() {
      return this.value.vehicleId;
    }
    get vehicleKey() {
      return displayOrderVehicleIdentifier(this.value);
    }
    get vehicleCount() {
      return this.value.vehicleCount;
    }
    get orderType() {
      return this.value.orderType;
    }
    get orderStatus() {
      return this.value.orderStatus;
    }
    get isNewCustomer() {
      return this.value.isNewCustomer;
    }
    get isEmployee() {
      return this.value.isEmployee;
    }
    get totalTaxable() {
      return this.value.totalTaxable;
    }
    get totalMarkDowns() {
      return this.value.totalMarkDowns;
    }
    get totalMarkUps() {
      return this.value.totalMarkUps;
    }
    get subTotal() {
      return this.value.subTotal;
    }
    get taxTotal() {
      return this.value.taxTotal;
    }
    get orderTotal() {
      return this.value.orderTotal;
    }
    get createdAt() {
      return this.value.createdAt;
    }
    get updatedAt() {
      return this.value.updatedAt;
    }
    get submissionAt() {
      return this.value.submissionAt;
    }
    get submissionDate() {
      return this.value.submissionDate;
    }
    get submissionTime() {
      return this.value.submissionTime;
    }
    get refundedDate() {
      return this.value.refundedDate;
    }
    get canceledDate() {
      return this.value.canceledDate;
    }
    get holds() {
      return this.value.holds;
    }
    get items() {
      return this.value.items;
    }
    get payments() {
      return this.value.payments || [];
    }
    get markDowns() {
      return this.value.markDowns;
    }
    get markUps() {
      return this.value.markUps;
    }
    get cashTendered() {
      return this.value.cashTendered;
    }
    get changeDue() {
      return this.value.changeDue;
    }
    get balanceDue() {
      return this.value.balanceDue;
    }
    get orderNumber() {
      return this.value.orderNumber;
    }
    get xrefId() {
      return this.value.xrefId;
    }
    get receiptNumber() {
      return this.value.receiptNumber;
    }
    get birthAt() {
      return this.value.birthAt;
    }
    get gender() {
      return this.value.gender;
    }
    get deviceName() {
      return this.value.deviceName;
    }
    get mbrCouponIssuedName() {
      return this.value.mbrCouponIssuedName;
    }
    get memberExpiresAt() {
      return this.value.memberExpiresAt;
    }
    get orgOrderId() {
      return this.value.orgOrderId;
    }
    get laneType() {
      return this.value.laneType;
    }
    get totalMbrRedemptions() {
      return this.value.totalMbrRedemptions;
    }
    get totalFlockDiscounts() {
      return this.value.totalFlockDiscounts;
    }
    get completedIn() {
      return this.value.completedIn;
    }
    get refundedAt() {
      return this.value.refundedAt;
    }
    get canceledAt() {
      return this.value.canceledAt;
    }
    get connectedAccountId() {
      return this.value.connectedAccountId;
    }
    get accountCheckResponse() {
      return this.value.accountCheckResponse;
    }
    get hasExistingMembership() {
      return this.value.hasExistingMembership;
    }
  }
  QqObservableOrder.defaultFields = DefaultOrderFields;
  return QqObservableOrder;
})();
export { QqObservableOrder as QqOrder };
